import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../shared/bottom-nav/bottom-nav.service';
import { ToastService } from '../shared/services/toast.service';
import { ButtonStyles, ButtonTypes } from '../button/button.component';
import { ZiroIcons } from '../shared/models/ziro-icons.enum';
import { BreadcrumbsService } from '../shared/breadcrumbs/breadcrumbs.service';
import { ZiroFormAbstractDirective } from '../forms/ziro-form-abstract.directive';
import { HtmlInputType, ZiroTextConfig } from '../forms/fields/text/ziro-text.component';
import { ZiroFormConfig, ZiroFormsValidationState } from '../forms/ziro-forms-models';
import { Observable, Subscription } from 'rxjs';
import { CdrDumpResource } from './cdr-dump.resource';
import { ZiroFormStateService } from '../forms/ziro-form-state.service';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { tap } from 'rxjs/operators';

export interface MockCdrRequest {
  startDate: string;
  endDate: string;
  callRecords: number;
  unusedPercentage: number;
}

@Component({
  selector: 'ziro-cdr-dump',
  templateUrl: './cdr-dump.component.html',
  styleUrls: ['./cdr-dump.component.scss'],
  providers: [CdrDumpResource],
  standalone: false,
})
export class ZiroCdrDumpComponent extends ZiroFormAbstractDirective<MockCdrRequest> implements OnInit, OnDestroy {
  protected bottomNavService = inject(BottomNavService);
  protected toastService = inject(ToastService);
  private breadcrumbsService = inject(BreadcrumbsService);
  private cdrDumpResource = inject(CdrDumpResource);
  protected _ziroFormStateService: ZiroFormStateService;
  private translateService = inject(TranslateService);

  ziroIcons = ZiroIcons;
  private _validators = {
    minValueValidator: (value: number, minValue: number): ZiroFormsValidationState => {
      return !isNaN(value) && Number(value) < minValue
        ? ZiroFormsValidationState.INVALID
        : ZiroFormsValidationState.VALID;
    },
    maxValueValidator: (value: number, maxValue: number): ZiroFormsValidationState => {
      return !isNaN(value) && Number(value) > maxValue
        ? ZiroFormsValidationState.INVALID
        : ZiroFormsValidationState.VALID;
    },
    beforeDateValidator: (value: number, endDate: string): ZiroFormsValidationState => {
      return moment(value).isBefore(endDate) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
    },
    afterDateValidator: (value: number, startDate: string): ZiroFormsValidationState => {
      return moment(value).isAfter(startDate) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
    },
  };
  formConfig = {
    fields: {
      startDate: {
        label: 'tkey;cdr_dump.start_date.label',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.DATE }),
        required: true,
        helpText: 'tkey;cdr_dump.start_date.helptext',
        validation: [
          {
            validator: (value: number) => {
              return this._validators.beforeDateValidator(value, this.formData.endDate);
            },
            message: () => this.translateService.instant('tkey;cdr_dump.start_date.validation.before'),
          },
        ],
      },
      endDate: {
        label: 'tkey;cdr_dump.end_date.label',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.DATE }),
        required: true,
        helpText: 'tkey;cdr_dump.end_date.helptext',
        validation: [
          {
            validator: (value: number) => {
              return this._validators.afterDateValidator(value, this.formData.startDate);
            },
            message: () => this.translateService.instant('tkey;cdr_dump.end_date.validation.after'),
          },
        ],
      },
      callRecords: {
        label: 'tkey;cdr_dump.call_records.label',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER }),
        required: true,
        helpText: 'tkey;cdr_dump.call_records.helptext',
        validation: [
          {
            validator: (value: number) => {
              return this._validators.minValueValidator(value, 0);
            },
            message: () =>
              this.translateService.instant('tkey;validators.global.error.min', {
                minValue: 0,
              }),
          },
        ],
      },
      unusedPercentage: {
        label: 'tkey;cdr_dump.unused_percentage.label',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER }),
        required: true,
        helpText: 'tkey;cdr_dump.unused_percentage.helptext',
        validation: [
          {
            validator: (value: number) => {
              return this._validators.minValueValidator(value, 0);
            },
            message: () =>
              this.translateService.instant('tkey;validators.global.error.min', {
                minValue: 0,
              }),
          },
          {
            validator: (value: number) => {
              return this._validators.maxValueValidator(value, 100);
            },
            message: () =>
              this.translateService.instant('tkey;validators.global.error.max', {
                maxValue: 100,
              }),
          },
        ],
      },
    },
  } as ZiroFormConfig;

  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;cdr_dump.title' }]);
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'generateCdrDump',
          label: 'tkey;cdr_dump.card.title',
          buttonClass: ButtonStyles.PRIMARY,
          dataAutomation: 'generateCdrDump',
          state: {
            pending: false,
            buttonDisableState: { disabled: false, tooltipKey: '' },
          },
          icon: this.ziroIcons.DOWNLOAD,
          type: ButtonTypes.SUBMIT,
          submitSubject: this._validateAndSubmitSource,
        },
      ]),
    );

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() =>
      this._setBottomNavValidationError(!this.isFormValid()),
    );
    this._subscriptions.add(formSubmittedSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
    this.breadcrumbsService.clearBreadcrumbs();
  }

  protected submit(): Observable<void> {
    return this._generateCdrDumpFile();
  }

  private _generateCdrDumpFile(): Observable<void> {
    this._setBottomNavPending(true);
    this._setBottomNavValidationError(false);

    return this.cdrDumpResource.generateCdrs(this.formData).pipe(
      tap(() => {
        this._setBottomNavPending(false);
      }),
    );
  }

  private _setBottomNavPending(setting: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'generateCdrDump',
        state: {
          pending: setting,
          buttonDisableState: {
            disabled: setting,
          },
        },
      }),
    );
  }

  private _setBottomNavValidationError(hasError: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: hasError,
      }),
    );
  }
}
